<template>
  <vue-auth-main>
    <template v-slot:tool>
      <div class="d-flex justify-content-end align-items-center flex-grow-1 mx-3">
        <Search class="te-search-sm" @search="search" />
      </div>
    </template>
    <template v-slot:main>
      <ListFrame ref="listFrame">
        <template v-slot:toolbox>
          <ListToolBox :total-count="totalListCount" :options="listToolBoxOptions" @add="add" @reload="reload" />
        </template>
        <template v-slot:content>
          <ListSelectedBar class="d-none" :selected="selected" :options="listSelectedBarOptions" @cancel="selected = []" @batch-delete="batchDelete" />
          <ListTable
            v-model="selected"
            :listdata="listdata"
            :fields="fields"
            :custom-cell-fields="customCellFields"
            :sort="sort"
            :options="listTableOptions"
            :get-item-index="getItemIndex"
            :get-item-key="getItemKey"
            @data-load="dataLoad"
          >
            <template v-slot:toolbar-content="{ item }">
              <ListModuleTableToolBar :options="listTableToolBarOptions(item)" @delete="deleteItem(item)" @edit="editItem(item)" @enable="enable(item)" @disable="disable(item)" />
            </template>
            <template v-slot:status="{ row }">
              <div class="d-flex align-items-center">
                <template v-if="row.item.status">
                  <span class="material-icons font-size-xs text-success mr-1">lens</span>
                  <span>{{ $t('auth.activating') }}</span>
                </template>
                <template v-else>
                  <span class="material-icons font-size-xs text-gray500 mr-1">lens</span>
                  <span>{{ $t('auth.disabled') }}</span>
                </template>
              </div>
            </template>
          </ListTable>
          <ListPagination v-model="currentPage" :pages="pages" />
        </template>
      </ListFrame>
    </template>
    <CompanySetting
      v-if="editMode !== ''"
      :company-id="editCompanyId"
      :mode="editMode"
      @saved="savedEdit"
      @close="closeEdit"
      @delete="deleteItem"
    />
  </vue-auth-main>
</template>
<script>
import {
  ListModuleFrame as ListFrame,
  ListModulePagination as ListPagination,
  ListModuleSelectedBar as ListSelectedBar,
  ListModuleToolBox as ListToolBox,
  ListModuleTable as ListTable,
  ListModuleTableToolBar
} from '@telexpress/vue-list-module';
import CompanySetting from '@/components/Admin/CompanySetting.vue';
import PermissionService from '@/services/PermissionService';
import DateToFormatString from '@/utils/DateToFormatString';
import Search from '@/components/Search.vue';

export default {
  components: {
    ListFrame,
    ListTable,
    ListToolBox,
    ListPagination,
    ListSelectedBar,
    ListModuleTableToolBar,
    CompanySetting,
    Search
  },
  data() {
    return {
      // settings
      listSelectedBarOptions: {
        buttons: ['cancel'],
        tools: ['delete']
      },
      listToolBoxOptions: {
        buttons: ['add'],
        tools: ['reload']
      },
      listTableOptions: {
        noLocalSorting: true,
        noSortReset: true
      },
      listCount: 20, // 每頁個數

      selected: [],
      sort: {
        key: 'name',
        desc: false
      },
      customCellFields: [
        {
          key: 'status', label: this.$t('auth.status'), class: 'align-middle', thClass: 'border-0'
        }
      ],
      currentPage: 1,
      keyword: '',

      listdata: [],
      totalListCount: 0,

      editMode: '',
      editCompanyId: ''
    };
  },
  computed: {
    fields() {
      const fields = [
        {
          key: 'selected', label: '', class: 'align-middle text-center', thClass: 'border-0', thStyle: { width: '48px' }
        },
        {
          key: 'name', label: '公司名稱', class: 'align-middle', thClass: 'border-0', sortable: true
        },
        {
          key: 'activateTime',
          label: '生效時間',
          class: 'text-right align-middle',
          thClass: 'border-0',
          sortable: true,
          formatter: (value) => {
            if ((value || 0) === 0) {
              return '';
            }
            return DateToFormatString(new Date(value), 'yyyy/MM/dd');
          }
        },
        {
          key: 'expireTime',
          label: '到期時間',
          class: 'text-right align-middle',
          thClass: 'border-0',
          sortable: true,
          formatter: (value) => {
            if ((value || 0) === 0) {
              return '';
            }
            return DateToFormatString(new Date(value), 'yyyy/MM/dd');
          }
        },
        {
          key: 'status', label: this.$t('auth.status'), class: 'align-middle align-middle', thClass: 'border-0', thAttr: { width: '15%' }, sortable: true
        }
      ];

      if (!this.editMode) {
        fields.push({
          key: 'toolbar', label: '', class: 'align-middle text-nowrap td-toolbar', tdClass: 'position-relative', thClass: 'border-0'
        });
      }

      return fields;
    },
    pages() {
      const pages = Math.ceil(this.totalListCount / this.listCount);
      return pages > 0 ? pages : 1;
    }
  },
  watch: {
    currentPage() {
      this.LoadListDataAsync();
    },
    sort(val, oldVal) {
      if (val.key !== oldVal.key || val.desc !== oldVal.desc) {
        this.LoadListDataAsync();
      }
    }
  },
  created() {
    this.LoadListDataAsync();
  },
  methods: {
    async LoadListDataAsync() {
      await this.$loading(async () => {
        const offset = (this.currentPage - 1) * this.listCount;
        const result = await PermissionService.GetCompaniesAsync(this.keyword, this.listCount, offset, this.sort.key, this.sort.desc);
        this.listdata = (result || {}).data || [];
        this.totalListCount = (result || {}).totalCount || 0;
      });
    },
    getItemIndex(item) {
      return this.selected.indexOf(item.id);
    },
    getItemKey(item) {
      return item.id;
    },
    async batchDelete() {
      await this.$loading(async () => {
        try {
          await PermissionService.DeleteCompaniesAsync(this.selected);
          this.$notify.deleteSuccess();
          this.selected = [];
          this.LoadListDataAsync();
        } catch (err) {
          this.$notify.deleteFailed();
        }
      });
    },
    reload() {
      this.LoadListDataAsync();
    },
    add() {
      this.editMode = 'add';
      this.openEdit();
    },
    async deleteItem(item) {
      const confirm = await this.$popup.confirmAsync(this.$t('auth.roleEdit.confirmDelete'));
      if (confirm) {
        await this.$loading(async () => {
          try {
            await PermissionService.DeleteCompanyAsync(item.id);
            this.$notify.deleteSuccess();
            const index = this.selected.indexOf(item.id);
            if (index > -1) this.selected.splice(index, 1);
            if (this.editCompanyId === item.id) this.closeEdit();
            this.LoadListDataAsync();
          } catch (err) {
            this.$notify.deleteFailed();
          }
        });
      }
    },
    savedEdit() {
      this.closeEdit();
      this.LoadListDataAsync();
    },
    dataLoad(options) {
      this.sort = {
        key: options.sortBy || '',
        desc: options.sortDesc || false
      };
    },
    editItem(item) {
      this.editMode = 'edit';
      this.openEdit(item.id);
    },
    async enable(item) {
      await this.$loading(async () => {
        try {
          await PermissionService.UpdateCompanyStatusAsync({ id: item.id, status: true });
          this.$notify.saveSuccess();
          const index = this.selected.indexOf(item.id);
          if (index > -1) this.selected.splice(index, 1);
          if (this.editCompanyId === item.id) this.closeEdit();
          this.LoadListDataAsync();
        } catch (err) {
          this.$notify.saveFailed();
        }
      });
    },
    async disable(item) {
      await this.$loading(async () => {
        try {
          await PermissionService.UpdateCompanyStatusAsync({ id: item.id, status: false });
          this.$notify.saveSuccess();
          const index = this.selected.indexOf(item.id);
          if (index > -1) this.selected.splice(index, 1);
          if (this.editCompanyId === item.id) this.closeEdit();
          this.LoadListDataAsync();
        } catch (err) {
          this.$notify.saveFailed();
        }
      });
    },
    search(val) {
      if (this.keyword !== val) {
        this.keyword = val;
        this.LoadListDataAsync();
      }
    },
    openEdit(id = '') {
      this.editCompanyId = id;
    },
    closeEdit() {
      this.editMode = '';
    },
    listTableToolBarOptions(item) {
      const options = {
        tools: ['edit', item.status ? 'disable' : 'enable', 'delete']
      };
      return options;
    }
  }
};
</script>
